import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Mail, Phone, MapPin, Clock, Calendar, Edit3,
  Stethoscope, CalendarCheck, AlertCircle, User
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAdmin } from '@/contexts/AdminContext';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import BookingDetail from '@/components/admin/BookingDetail';

const SITES = {
  shihab_romeed: { label: 'SR', full: 'Shihab Romeed', color: 'bg-stone-100 text-stone-700 border-stone-200' },
  specialist_dental_london: { label: 'SDL', full: 'Specialist Dental London', color: 'bg-[#C8A86B]/15 text-[#96793E] border-[#C8A86B]/30' },
  london_dental_academy: { label: 'LDA', full: 'London Dental Academy', color: 'bg-blue-50 text-blue-700 border-blue-200' },
};

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const STATUS_STYLES = {
  confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  cancelled: 'bg-red-50 text-red-600 border-red-200',
  completed: 'bg-blue-50 text-blue-700 border-blue-200',
  no_show: 'bg-red-100 text-red-800 border-red-300',
};

function collapseSlots(slots) {
  if (!slots || slots.length === 0) return [];
  const sorted = [...slots].sort();
  const intervals = [];
  let start = sorted[0], end = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    const [eh, em] = end.split(':').map(Number);
    const [sh, sm] = sorted[i].split(':').map(Number);
    if (sh * 60 + sm === eh * 60 + em + 15) {
      end = sorted[i];
    } else {
      const [fh, fm] = end.split(':').map(Number);
      const endM = fh * 60 + fm + 15;
      intervals.push(`${start}–${String(Math.floor(endM / 60)).padStart(2, '0')}:${String(endM % 60).padStart(2, '0')}`);
      start = sorted[i];
      end = sorted[i];
    }
  }
  const [fh, fm] = end.split(':').map(Number);
  const endM = fh * 60 + fm + 15;
  intervals.push(`${start}–${String(Math.floor(endM / 60)).padStart(2, '0')}:${String(endM % 60).padStart(2, '0')}`);
  return intervals;
}

export default function ProviderProfile() {
  const { id } = useParams();
  const { apiFetch } = useAdmin();
  const [provider, setProvider] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBookingId, setSelectedBookingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [provRes, bookRes] = await Promise.all([
        apiFetch(`/providers/${id}`),
        apiFetch(`/bookings?providerId=${id}&take=50`),
      ]);
      const provData = await provRes.json();
      const bookData = await bookRes.json();
      setProvider(provData.provider || null);
      setBookings(bookData.items || []);
    } catch {
      toast.error('Failed to load provider');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, id]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="max-w-6xl space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid lg:grid-cols-3 gap-6">
          <Skeleton className="h-[300px] rounded-2xl lg:col-span-1" />
          <Skeleton className="h-[300px] rounded-2xl lg:col-span-2" />
        </div>
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="max-w-6xl">
        <Card className="border-stone-200 shadow-sm">
          <CardContent className="p-12 text-center">
            <AlertCircle className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-stone-900">Provider Not Found</h3>
            <Link to="/admin/providers"><Button className="mt-6">Back to Providers</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Compute stats
  const now = new Date();
  const todayStr = now.toISOString().split('T')[0];
  const upcomingBookings = bookings
    .filter(b => b.status === 'confirmed' && b.confirmedDate && b.confirmedDate >= todayStr)
    .sort((a, b) => (a.confirmedDate + a.confirmedTime) > (b.confirmedDate + b.confirmedTime) ? 1 : -1);
  const completedCount = bookings.filter(b => b.status === 'completed').length;
  const totalRevenue = bookings.reduce((sum, b) => sum + (b.amountPaid || 0), 0);
  const noShowCount = bookings.filter(b => b.status === 'no_show').length;

  // Build schedule summary per site
  const schedulesBySite = {};
  (provider.schedules || []).forEach(s => {
    if (!schedulesBySite[s.siteKey]) schedulesBySite[s.siteKey] = {};
    schedulesBySite[s.siteKey][s.dayOfWeek] = s.timeSlots;
  });

  return (
    <div className="max-w-6xl pb-20">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Link to="/admin/providers" className="h-10 w-10 rounded-full border border-stone-200 bg-white flex items-center justify-center hover:bg-stone-50 transition-colors shadow-sm shrink-0">
          <ArrowLeft className="w-4 h-4 text-stone-600" />
        </Link>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-stone-400">Provider Profile</p>
          <h2 className="text-2xl md:text-3xl font-playfair font-medium text-stone-900 tracking-tight truncate">
            {provider.name}
          </h2>
        </div>
        <div className="flex gap-2 shrink-0">
          <Link to={`/admin/providers/${id}/edit`}>
            <Button variant="outline" className="h-10 gap-2 border-stone-200">
              <Edit3 className="w-4 h-4" /> Edit
            </Button>
          </Link>
          <Link to={`/admin/schedule/${id}`}>
            <Button className="h-10 gap-2 bg-stone-900 text-white hover:bg-stone-800">
              <Clock className="w-4 h-4" /> Manage Schedule
            </Button>
          </Link>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column — Profile Info */}
        <div className="space-y-5">
          <Card className="border-stone-200 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-b from-stone-50 to-white p-6 border-b border-stone-100">
              <div className="h-16 w-16 rounded-2xl bg-stone-900 flex items-center justify-center text-white font-playfair font-medium text-2xl shadow-inner mb-4">
                {(provider.name || 'P').charAt(0)}
              </div>
              <h3 className="font-playfair text-xl font-medium text-stone-900">{provider.name}</h3>
              <p className="text-sm text-stone-500 mt-0.5">{provider.title || 'Clinician'}</p>
            </div>
            <CardContent className="p-5 space-y-4">
              {provider.email && (
                <div className="flex items-center gap-3 text-sm text-stone-700">
                  <Mail className="w-4 h-4 text-stone-400 shrink-0" />
                  <span className="truncate">{provider.email}</span>
                </div>
              )}
              {provider.phone && (
                <div className="flex items-center gap-3 text-sm text-stone-700">
                  <Phone className="w-4 h-4 text-stone-400 shrink-0" />
                  <span>{provider.phone}</span>
                </div>
              )}
              <div className="flex items-center gap-3 text-sm text-stone-700">
                <MapPin className="w-4 h-4 text-stone-400 shrink-0" />
                <div className="flex flex-wrap gap-1.5">
                  {provider.sites?.map(s => (
                    <span key={s} className={cn('px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border', SITES[s]?.color || 'bg-stone-100')}>
                      {SITES[s]?.label || s}
                    </span>
                  ))}
                </div>
              </div>
              {provider.specialties?.length > 0 && (
                <div className="pt-3 border-t border-stone-100">
                  <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider mb-2">Specialties</p>
                  <div className="flex flex-wrap gap-1.5">
                    {provider.specialties.map((s, i) => (
                      <span key={i} className="px-2 py-1 bg-stone-100 text-stone-700 text-[11px] font-medium rounded-md">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {provider.bio && (
                <div className="pt-3 border-t border-stone-100">
                  <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider mb-2">Bio</p>
                  <p className="text-sm text-stone-600 leading-relaxed">{provider.bio}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3">
            <StatCard label="Upcoming" value={upcomingBookings.length} icon={CalendarCheck} />
            <StatCard label="Completed" value={completedCount} icon={Calendar} />
            <StatCard label="Revenue" value={`\u00A3${totalRevenue.toLocaleString()}`} icon={Stethoscope} />
            <StatCard label="No-shows" value={noShowCount} icon={AlertCircle} warn={noShowCount > 0} />
          </div>
        </div>

        {/* Right Column — Schedule + Bookings */}
        <div className="lg:col-span-2 space-y-6">
          {/* Weekly Schedule Summary */}
          <Card className="border-stone-200 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-stone-900 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-stone-400" /> Weekly Schedule
                </h3>
                <Link to={`/admin/schedule/${id}`} className="text-xs font-medium text-stone-500 hover:text-stone-900 transition-colors">
                  Edit
                </Link>
              </div>
              {Object.keys(schedulesBySite).length === 0 ? (
                <p className="text-sm text-stone-400 italic">No schedule configured.</p>
              ) : (
                <div className="space-y-4">
                  {Object.entries(schedulesBySite).map(([siteKey, days]) => (
                    <div key={siteKey}>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400 mb-2">
                        {SITES[siteKey]?.full || siteKey}
                      </p>
                      <div className="grid grid-cols-7 gap-1.5">
                        {[0, 1, 2, 3, 4, 5, 6].map(dayIdx => {
                          const slots = days[dayIdx];
                          const hasSlots = slots && slots.length > 0;
                          const intervals = hasSlots ? collapseSlots(slots) : [];
                          return (
                            <div key={dayIdx} className={cn(
                              'rounded-lg p-2 text-center border transition-colors',
                              hasSlots ? 'bg-emerald-50 border-emerald-200' : 'bg-stone-50 border-stone-200'
                            )}>
                              <p className={cn('text-[10px] font-bold uppercase', hasSlots ? 'text-emerald-700' : 'text-stone-400')}>
                                {DAY_LABELS[dayIdx]}
                              </p>
                              {hasSlots && intervals.map((int, i) => (
                                <p key={i} className="text-[9px] text-emerald-600 mt-0.5 font-medium">{int}</p>
                              ))}
                              {!hasSlots && <p className="text-[9px] text-stone-400 mt-0.5">Off</p>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Bookings */}
          <Card className="border-stone-200 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-stone-900 flex items-center gap-2">
                  <CalendarCheck className="w-4 h-4 text-stone-400" /> Upcoming Bookings
                </h3>
                <span className="text-xs text-stone-500">{upcomingBookings.length} scheduled</span>
              </div>
              {upcomingBookings.length === 0 ? (
                <p className="text-sm text-stone-400 italic py-4 text-center">No upcoming bookings.</p>
              ) : (
                <div className="space-y-2">
                  {upcomingBookings.slice(0, 10).map(b => (
                    <motion.button
                      key={b.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      onClick={() => setSelectedBookingId(b.id)}
                      className="w-full text-left flex items-center gap-3 p-3 rounded-xl border border-stone-200 hover:border-stone-300 hover:bg-stone-50/50 transition-all group"
                    >
                      <div className="h-9 w-9 rounded-full bg-stone-900 flex items-center justify-center text-white text-[10px] font-bold shrink-0">
                        {b.patient?.firstName?.charAt(0)}{b.patient?.lastName?.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-stone-900 truncate">
                          {b.patient?.firstName} {b.patient?.lastName}
                        </p>
                        <p className="text-xs text-stone-500 truncate">{b.service}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-xs font-semibold text-stone-900">
                          {new Date(b.confirmedDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                        </p>
                        <p className="text-[11px] text-stone-500">{b.confirmedTime}</p>
                      </div>
                    </motion.button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent History */}
          <Card className="border-stone-200 shadow-sm">
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold text-stone-900 flex items-center gap-2 mb-4">
                <Calendar className="w-4 h-4 text-stone-400" /> Recent Bookings
              </h3>
              {bookings.length === 0 ? (
                <p className="text-sm text-stone-400 italic py-4 text-center">No booking history.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-stone-100">
                        <th className="text-left px-3 py-2 text-xs font-semibold text-stone-500 uppercase">Patient</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-stone-500 uppercase">Service</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-stone-500 uppercase">Status</th>
                        <th className="text-right px-3 py-2 text-xs font-semibold text-stone-500 uppercase">Amount</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-stone-500 uppercase">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {bookings.slice(0, 15).map(b => (
                        <tr
                          key={b.id}
                          onClick={() => setSelectedBookingId(b.id)}
                          className="hover:bg-stone-50/50 transition-colors cursor-pointer"
                        >
                          <td className="px-3 py-2.5 text-stone-800 font-medium">
                            {b.patient?.firstName} {b.patient?.lastName}
                          </td>
                          <td className="px-3 py-2.5 text-stone-600 truncate max-w-[180px]">{b.service}</td>
                          <td className="px-3 py-2.5">
                            <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border capitalize', STATUS_STYLES[b.status] || 'bg-stone-50')}>
                              {b.status?.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-right font-semibold text-stone-900">
                            {b.amountCharged != null ? `\u00A3${b.amountCharged}` : '—'}
                          </td>
                          <td className="px-3 py-2.5 text-xs text-stone-500">
                            {b.confirmedDate
                              ? new Date(b.confirmedDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
                              : new Date(b.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Booking Detail Slide-over */}
      {selectedBookingId && (
        <BookingDetail
          bookingId={selectedBookingId}
          onClose={() => setSelectedBookingId(null)}
          onUpdate={load}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, warn }) {
  return (
    <div className={cn(
      'rounded-xl border p-3.5 shadow-sm',
      warn ? 'bg-red-50 border-red-200' : 'bg-white border-stone-200'
    )}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={cn('w-3.5 h-3.5', warn ? 'text-red-400' : 'text-stone-400')} />
        <span className={cn('text-[10px] font-semibold uppercase tracking-wider', warn ? 'text-red-500' : 'text-stone-400')}>{label}</span>
      </div>
      <p className={cn('text-lg font-bold', warn ? 'text-red-700' : 'text-stone-900')}>{value}</p>
    </div>
  );
}
